/* eslint-disable max-lines */
import PropTypes from "prop-types";
import get from "lodash.get";
import isEmpty from "lodash.isempty";
import Typography from "../../atoms/typography";
import InformationButton from "../../molecules/informationButton";
import { formatToK, onSmoothScrollToElement, updateCurrency } from "../../../utils/common";
import { shallowEqual, useSelector } from "react-redux";
import { constants, CTapCampaignConstants } from "../../../constants/pageConstants/pdpConstants";
import {
  BEST_RATING,
  COUNTRIES,
  DETAILS,
  INCLUSIVE_OF_ALL_TAXES,
  MAXIMUM_RETAIL_PRICE,
  PRICE,
} from "../../../constants/common/common";
import Brick from "../../atoms/brick";
import { useEffect, useState } from "react";
import { campaignEvents } from "../../../analytics/clevertapConstants";
import useAnalytics from "../../../analytics/useAnalytics";
import dynamic from "next/dynamic";
import EggVariantSwitch from "../eggVariantSwitch";

const PDPSaleNudge = dynamic(() => import("../pdpSaleNudge"), {
  ssr: false,
});
import FreeDeliveryChip from "../FreeDeliveryChip";
import { getDisplayTags } from "../../../utils/plp/common";
import ProductTagFlipper from "../productTagFlipper";
import ProductTag from "../../atoms/ProductTag";
import { StarIcon } from "@phosphor-icons/react/dist/ssr";
import { ShareNetworkIcon } from "@phosphor-icons/react";

const FREE_DELIVERY_SLIDE = { id: "freeDelivery", isCustomSlide: true };

/**
 * Inserts each custom slide into `tags` at its 1-based `position` (1 = first slot) - a literal
 * slot index in the final list, independent of whatever (possibly missing or CMS-arbitrary)
 * `.position` field the real tags carry. Slides are inserted in ascending position order so
 * multiple custom slides each land in the slot they asked for. A slide with no position (or one
 * beyond the list) is appended after everything else.
 *
 * @param {Array} tags - CMS-driven display tags.
 * @param {Array} customSlides - Custom slides to insert, each `{ position, ... }`.
 * @returns {Array}
 */
const mergeTagsWithCustomSlides = (tags, customSlides) => {
  if (!customSlides.length) return tags;

  /** Converts a 1-based `position` into a 0-based splice index, clamped to `listLength`. */
  const toSlotIndex = (position, listLength) =>
    Number.isInteger(position) && position > 0 ? Math.min(position - 1, listLength) : listLength;

  const sortedSlides = [...customSlides].sort(
    (a, b) => toSlotIndex(a.position, Infinity) - toSlotIndex(b.position, Infinity),
  );

  return sortedSlides.reduce((list, slide) => {
    const next = [...list];
    next.splice(toSlotIndex(slide.position, list.length), 0, slide);
    return next;
  }, tags);
};

/**
 * ProductPriceSection Component
 *
 * Renders the product details section including the product name, price, discount, and review information.
 *
 * @component
 * @param {object} props - Component properties.
 * @param {string} props.containerPadding - Custom padding classes for the container.
 * @param {object} props.currentItem - The current product data object.
 * @param {string} props.selectedCurrency - The selected currency code.
 * @param {boolean} props.isMobile - Flag to determine if the view is mobile.
 * @param {object} props.serverData - Server-side data for the product.
 * @param {object} props.serverCustomerReviewData - Server-side customer review and rating data.
 * @param {boolean} [props.isOutOfStock=false] - Flag to indicate if the product is out of stock.
 * @param {boolean} [props.isInternational=false] - Flag to indicate if the product is for international shipping.
 * within the display tags (1 = first); appended at the end when omitted or out of range.
 * @param {Array<{id: string, position?: number, render: Function}>} [props.customTags] - Extra custom
 * slides to merge into the display tags, each with its own 1-based `position` and `render` function.
 * @returns {JSX.Element}
 */
const ProductPriceSection = ({
  containerPadding,
  currentItem,
  selectedCurrency,
  isMobile,
  serverData,
  serverCustomerReviewData = {},
  isOutOfStock,
  saleNudgeConfig,
  monthlyOrdersCount,
  productsAttrTagResponse,
  isShowFreeDeliveryTag,
  customTags = [],
  isHideTitleRating,
  isHidePriceAndOrderCount,
}) => {
  const { updatedDeliveryDatesAndGeoPrice, catalogId, priceDetails } = useSelector(
    (state) => ({
      updatedDeliveryDatesAndGeoPrice: state?.shippingDateAndSlotSelection?.updatedDeliveryDatesAndGeoPrice,
      priceDetails: state?.shippingDateAndSlotSelection?.priceDetails,
      catalogId: state.appConfig.catalogId,
    }),
    shallowEqual,
  );

  const [freeGiftTag, setFreeGiftTag] = useState(null);
  const deliveryDatesAndGeoPrice = updatedDeliveryDatesAndGeoPrice;
  const { trackEvent } = useAnalytics();

  /** Render display tags for a product, merging in the free delivery chip and any custom tags */
  const renderDisplayTags = (data) => {
    const tags = getDisplayTags(productsAttrTagResponse, data);
    const showFreeDelivery = catalogId === COUNTRIES.INDIA && isShowFreeDeliveryTag;

    const customSlides = [
      ...(showFreeDelivery ? [{ ...FREE_DELIVERY_SLIDE, position: 1 }] : []),
      ...customTags.map((tag) => ({ ...tag, isCustomSlide: true })),
    ];

    const displayTags = mergeTagsWithCustomSlides(tags, customSlides);

    if (!displayTags.length) return null;

    return (
      <div className="flex flex-1 gap-8">
        <ProductTagFlipper
          isFromPDP={false}
          productAttributes={displayTags}
          isMobile={isMobile}
          renderItem={(slide) => {
            if (slide?.id === FREE_DELIVERY_SLIDE.id) {
              return showFreeDelivery && <FreeDeliveryChip />;
            }
            if (slide?.isCustomSlide) {
              return slide.render?.();
            }
            return <ProductTag productAttributes={slide} isMobile={isMobile} isTruncate={true} isFromPDP={true} />;
          }}
        />
      </div>
    );
  };

  /**
   *
   * Handles the "CT_web_native_display" event and sets the appropriate state
   * based on the delivery topic.
   *
   * @param {object} event .
   */
  const handleEvent = (event) => {
    if (event.detail.kv.topic === CTapCampaignConstants?.PDP_FREE_GIFT_TOPIC_NAME) {
      trackEvent(campaignEvents.RENDER_NOTIFICATION_VIEWED, event?.detail, ["clevertap"]);
      setFreeGiftTag(event.detail.kv);
    }
  };

  const ratingValue = get(serverCustomerReviewData, "ratingValue");
  const totalReview = get(serverCustomerReviewData, "totalReview");
  const hasValidRating = ratingValue != null && ratingValue > 0;
  const hasValidReviewCount = totalReview != null && totalReview > 0;
  const shouldShowRatingSection = hasValidRating || hasValidReviewCount;
  const shouldShowSchema = hasValidRating && hasValidReviewCount;

  /** */
  const renderRatingSection = () => {
    return (
      <div
        {...(shouldShowSchema && {
          itemProp: "aggregateRating",
          itemScope: true,
          itemType: "https://schema.org/AggregateRating",
        })}
        role="presentation"
        className="flex cursor-pointer items-center gap-4 rounded-full border-[0.5px] border-solid border-greySoft px-6 py-4"
        onClick={() => onSmoothScrollToElement("reviews-section", 150)}
      >
        {shouldShowSchema && (
          <>
            <meta itemProp="ratingValue" content={ratingValue} />
            <meta itemProp="reviewCount" content={totalReview} />
            <meta itemProp="bestRating" content={BEST_RATING} />
          </>
        )}
        {hasValidRating && (
          <>
            <StarIcon size={16} weight="fill" color="#E3AF00" />
            <Typography variant="p" Tag={"p"} classes="cursor-pointer text-12 font-500 text-grey-500">
              {`${ratingValue}`}
            </Typography>
          </>
        )}
        {hasValidRating && hasValidReviewCount && <div className="w-[1px] self-stretch bg-grey-200" />}
        {hasValidReviewCount && (
          <div className="inline-flex text-grey-500">
            <Typography variant="p" Tag={"p"} className="text-12 font-500 text-grey-500">
              {formatToK(totalReview, true)}
            </Typography>
          </div>
        )}
      </div>
    );
  };

  useEffect(() => {
    document.addEventListener("CT_web_native_display", handleEvent);

    return () => {
      document.removeEventListener("CT_web_native_display", handleEvent);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** */
  const onShareClick = async () => {
    if (navigator.canShare) {
      await navigator.share({
        title: currentItem?.productName,
        text: "Check out this image!",
        url: window.location.href,
      });
    } else {
      alert("Sharing not supported on this device.");
    }
  };
  /**
   * Renders a div containing price information with details.
   * @returns {JSX.Element} The JSX element containing the price details.
   */
  const renderInformation = (listPrice) => {
    return (
      <>
        <p
          className={`mb-2 px-16 pb-12 pt-0 text-base font-600 md:px-0 md:py-0 ${isMobile ? "border-b border-lightGrey" : ""}`}
        >{`${PRICE} ${DETAILS}`}</p>
        <div className="p-16 md:p-0">
          <div className="flex items-center justify-between pt-3">
            <span className="text-14 font-400 text-black-100">{MAXIMUM_RETAIL_PRICE}</span>
            <span className="pl-24 text-14 font-500 text-black-900">{updateCurrency(selectedCurrency, listPrice)}</span>
          </div>
          <div className="text-sm text-gray-500">{INCLUSIVE_OF_ALL_TAXES}</div>
        </div>
      </>
    );
  };

  return (
    <div className={`relative flex flex-col gap-8 ${containerPadding}`}>
      {!isHideTitleRating && (
        <>
          <div className="flex items-start justify-between gap-8">
            <Typography
              Tag={"h1"}
              id="productName"
              classes="text-16 md:text-20 xxl:text-24 font-500 text-black-100 leading-6 md:line-clamp-2 min-w-0 flex-1"
              itemProp="name"
            >
              {get(currentItem, "productName")}
            </Typography>
            {isMobile && (
              <div
                onClick={onShareClick}
                className="flex h-[32px] w-[32px] items-center justify-center rounded-8 border border-greySoft p-6 hover:cursor-pointer"
              >
                <ShareNetworkIcon size={24} className="py-2 text-fnp-500" weight="bold" />
              </div>
            )}
            {!isMobile && shouldShowRatingSection && renderRatingSection()}
          </div>

          <div className="flex items-center justify-between gap-8">
            {renderDisplayTags(serverData?.productTags)} {shouldShowRatingSection && isMobile && renderRatingSection()}
          </div>
        </>
      )}

      {!isHidePriceAndOrderCount && (
        <>
          <div className="relative flex flex-wrap justify-between gap-8">
            <div className="relative flex flex-col gap-8">
              <div className="relative flex flex-wrap items-baseline gap-8 md:items-center">
                <Typography Tag={"p"} classes="text-16 md:text-24 xxl:text-28 font-600 text-fnp-500" id="odometer">
                  {updateCurrency(
                    selectedCurrency,
                    deliveryDatesAndGeoPrice?.price || priceDetails?.price || get(currentItem, "productPrice.price"),
                  )}
                </Typography>
                {(get(currentItem, "productPrice.listPrice") !== get(currentItem, "productPrice.price") ||
                  deliveryDatesAndGeoPrice?.price !== deliveryDatesAndGeoPrice?.listPrice) && (
                  <div className="flex w-[auto] gap-6">
                    <Typography Tag={"p"} classes="text-14 md:text-20 font-400 text-grey-400">
                      <del>
                        {updateCurrency(
                          selectedCurrency,
                          deliveryDatesAndGeoPrice?.listPrice ||
                            priceDetails?.price ||
                            get(currentItem, "productPrice.listPrice"),
                        )}
                      </del>
                    </Typography>
                  </div>
                )}
                <div className="relative top-3" itemProp="offers" itemScope={true} itemType="https://schema.org/Offer">
                  <meta itemProp="url" content={`/gift/${serverData?.urlIdentifier}`} />
                  <meta
                    itemProp="priceCurrency"
                    content={isEmpty(selectedCurrency) ? "INR" : selectedCurrency?.currency?.id}
                  />
                  <meta itemProp="price" content={get(currentItem, "productPrice.listPrice")} />
                  <link
                    itemProp="availability"
                    href={`http://schema.org/${!isOutOfStock ? "InStock" : "OutofStock"}`}
                  />
                  <InformationButton
                    isMobile={isMobile}
                    renderInformation={renderInformation(get(currentItem, "productPrice.listPrice"))}
                    icon={constants.infoCircleGraySvg}
                    rootId="productInformation"
                  />
                </div>
                {!isEmpty(freeGiftTag) && (
                  <Brick
                    content={freeGiftTag?.name}
                    icon={freeGiftTag?.icon}
                    customStyle={{
                      backgroundColor: freeGiftTag?.backgroundColor,
                      color: freeGiftTag?.fontColor,
                      border: "none",
                      borderRadius: "8px",
                      marginLeft: "12px",
                      padding: "4px 8px",
                    }}
                    contentClass="pl-4 text-10"
                  />
                )}
              </div>
              <PDPSaleNudge saleNudgeConfig={saleNudgeConfig?.data || {}} monthlyOrdersCount={monthlyOrdersCount} />
            </div>
            <div className={`${isMobile ? "pt-[5px]" : ""}`}>
              <EggVariantSwitch />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

ProductPriceSection.propTypes = {
  containerPadding: PropTypes.string.isRequired,
  currentItem: PropTypes.object.isRequired,
  selectedCurrency: PropTypes.object.isRequired,
  isMobile: PropTypes.bool.isRequired,
  serverData: PropTypes.object.isRequired,
  serverCustomerReviewData: PropTypes.object,
  isOutOfStock: PropTypes.bool,
  saleNudgeConfig: PropTypes.object,
  monthlyOrdersCount: PropTypes.number,
  productsAttrTagResponse: PropTypes.object.isRequired,
  isShowFreeDeliveryTag: PropTypes.bool,
  freeDeliveryTagPosition: PropTypes.number,
  customTags: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      position: PropTypes.number,
      render: PropTypes.func.isRequired,
    }),
  ),
  isHideTitleRating: PropTypes.bool,
  isHidePriceAndOrderCount: PropTypes.bool,
};

export default ProductPriceSection;
